import React, { useContext, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  FormHelperText,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Modal,
  TextField,
  Typography,
  useTheme
} from "@mui/material";

import { userDisplayName } from "../EventFunctions";

import * as Yup from "yup";
import { Formik } from "formik";
import { AuthContext } from "../../../../components/user/BCPAuthProvider";

import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import { postNotification } from "../../../../components/utilities/Notifications";
import ShareModal from "../../../../components/modals/ShareModal";

import moment from "moment";
import "moment-timezone";
import CountdownTimer from "../../../../components/utilities/CountdownTimer";

import CustomChip from "../../../../components/utilities/CustomChip";
import AuthorizeHostedForm from "../SubComponents/AuthorizeHostedForm";

import { useInView } from "react-intersection-observer";
import {
  getCurrencySymbol,
  getData,
  getTicketingFeeSummary,
  numberWithCommas,
  postData,
  wordStartsWithVowel
} from "../../../../components/utilities/UtilityFunctions";
import MarkdownViewer from "../../../../components/utilities/MarkdownViewer";
import {
  BCPDiscord,
  BCPEnvelope,
  BCPFacebook,
  BCPInstagram,
  BCPRotateRight,
  BCPShareNodes,
  BCPStar,
  BCPTwitch,
  BCPTwitterX,
  BCPXmark,
  BCPYoutube
} from "../../../../components/icons/bcp/BCPIcons";
import RedirectModal from "../../../../components/modals/RedirectModal";
import { useConfirm } from "material-ui-confirm";
import Login from "../../../user/Login";
import {
  getEnvironmentVariable,
  supportsUYPlatformFeatures
} from "../../../../global/environmentVariables";
import { isAppMode } from "../../../../global/AppOverlay";
import { getTicketing } from "../../../../global/GlobalVariables";
import GenericPicker from "../../../../components/utilities/GenericPicker";
import { handlePagingAll } from "../../../../components/api/APIUtilities";

import { SquareCheckout } from "./subcomponents/CheckoutForms";
import { ExpressCheckoutElement } from "@stripe/react-stripe-js";
import ModalLoadingMessage from "../../../../components/modals/ModalLoadingMessage";

const loadStripeAsync = async () => {
  try {
    const { loadStripe } = await import("@stripe/stripe-js");
    return await loadStripe(getEnvironmentVariable("STRIPE_PK"));
  } catch (error) {
    const retryData = sessionStorage.getItem("stripe_retry_data");
    const now = Date.now();

    if (retryData) {
      const { timestamp, attempts } = JSON.parse(retryData);
      const oneMinuteAgo = now - 60 * 1000;

      if (timestamp > oneMinuteAgo && attempts >= 1) {
        return null;
      }
    }

    sessionStorage.setItem(
      "stripe_retry_data",
      JSON.stringify({
        timestamp: now,
        attempts: 1
      })
    );

    window.location.reload();
    return null;
  }
};

const SUPPORTS_UY_PLATFORM_FEATURES = supportsUYPlatformFeatures();
const ENFORCE_ACCOUNT = getEnvironmentVariable("ENFORCE_ACCOUNT") === "true";

const fetchFile = async (fileId, size, imageCache) => {
  if (!fileId) return;

  if (imageCache) {
    if (!imageCache[size || 0]) {
      imageCache[size || 0] = {};
    }
    if (imageCache[size || 0][fileId]) {
      return imageCache[size || 0][fileId];
    }
  }

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {
        size: size
      }
    });

    if (imageCache) {
      imageCache[size || 0][fileId] = returnObj;
    }
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const ImagePreview = (props) => {
  const { name, imageFile, imageFileId, imageCache, imagePhotoUrl, event } =
    props;
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);

  useEffect(() => {
    const go = async () => {
      setLoading(true);

      if (!imageFileId && !imagePhotoUrl) {
        setLoading(false);
        setImage({
          url: `${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/images/EventImage${
            theme?.palette?.mode === "dark" ? "Dark" : ""
          }.png`
        });

        return;
      }

      if (imagePhotoUrl) {
        setLoading(false);
        setImage({
          url: imagePhotoUrl
        });

        return;
      }

      if (image) {
        if (image.id === imageFileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = imageFile
          ? imageFile
          : await fetchFile(imageFileId, "medium", imageCache);
        if (file) {
          setImage({
            id: imageFileId,
            ...file
          });
        }
      } catch (err) {
        //
      }
    };

    go();
    // eslint-disable-next-line
  }, [imageFileId]);

  return (
    <>
      <Box
        sx={{
          maxWidth: "100%",
          height: "calc(100vw / 16 * 9)",
          maxHeight: "300px",
          justifyContent: "center",
          alignItems: "center",
          position: "relative"
        }}
      >
        {image?.url && (
          <img
            src={image.url}
            alt={name}
            style={{
              maxWidth: "100%",
              height: "100%",
              maxHeight: "100%"
            }}
            onLoad={() => {
              setLoading(false);
            }}
          />
        )}

        {loading && (
          <Box
            sx={{
              width: "100%",
              position: "relative",
              textAlign: "center",
              top: "-60%"
            }}
          >
            <CircularProgress size={50} />
          </Box>
        )}

        {(event?.flags?.testEvent === true || event?.testEvent === true) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 150,
              height: 150,
              overflow: "hidden",
              pointerEvents: "none",
              zIndex: 2
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 25,
                right: -35,
                width: 180,
                backgroundColor: theme.palette.warning.main,
                color: theme.palette.warning.contrastText,
                textAlign: "center",
                transform: "rotate(45deg)",
                transformOrigin: "center",
                py: 0.5,
                boxShadow: 3
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase"
                }}
              >
                Test Event
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default function EventAboutDefault(props) {
  const {
    handleTabChange,
    tabIndexes,
    event,
    refreshEvent,
    setEvent,
    followEvent,
    following,
    registerParam
  } = props;

  const authContext = useContext(AuthContext);
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventDetails"
  });
  const { t: tGeneral } = useTranslation();
  const [myWarhammerPrompt, setMyWarhammerPrompt] = useState(false);

  // TODO: Add timezone display - revisit this once timezone is available per user
  // const getTimezoneDisplay = (date) => {
  //   const locale = getLocalStorage("locale");
  //   const now = moment();
  //   const isDST = now.isDST();

  //   switch(locale) {
  //     case "en-gb":
  //       return isDST ? "BST" : "GMT";
  //     case "en-us":
  //       return moment.tz(moment.tz.guess()).format("z");
  //     case "es-mx":
  //       return isDST ? "CDT" : "CST";
  //     case "zh-cn":
  //       return "UTC" + moment(date).format("Z");
  //     case "de":
  //       return isDST ? "MESZ" : "MEZ";
  //     default:
  //       return moment(date).format("z");
  //   }
  // };

  const theme = useTheme();

  const [loading, setLoading] = useState(false);

  const [eventUsers, setEventUsers] = useState(null);

  const [rightBoxRef, rightBoxInView] = useInView({
    threshold: 0,
    rootMargin: "0px 0px -50px 0px"
  });

  const [ticketModal, setTicketModal] = useState(null);
  const [contactTOModal, setContactTOModal] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [leavingModalOpen, setLeavingModalOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const appMode = isAppMode();

  const confirm = useConfirm();

  const handleRegisterClick = async () => {
    if (ENFORCE_ACCOUNT && !authContext?.authState?.identity?.userId) {
      postNotification("info", tGeneral("notifications.info.pleaseLogin"));
      setShowLogin(true);
    } else {
      setTicketModal(1);
    }
  };

  useEffect(() => {
    var apiEventUsersGroups = [];
    var apiEventUsers = [];

    if (event?.eventUsers) {
      apiEventUsers = Object.values(event?.eventUsers);

      apiEventUsers.sort((a, b) => {
        if (
          a.role?.orderNum !== undefined &&
          b.role?.orderNum !== undefined &&
          a.role?.orderNum !== b.role?.orderNum
        ) {
          return a.role?.orderNum - b.role?.orderNum;
        }
        if (
          a.role?.name !== undefined &&
          b.role?.name !== undefined &&
          a.role?.name !== b.role?.name
        ) {
          var roleA = a.role.name.toLowerCase();
          var roleB = b.role.name.toLowerCase();
          if (roleA > roleB) return 1;
          if (roleA < roleB) return -1;
        }
        var nameA = userDisplayName(a.firstName, a.lastName).toLowerCase();
        var nameB = userDisplayName(b.firstName, b.lastName).toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });

      apiEventUsersGroups = {};
      apiEventUsers.forEach((eventUser) => {
        if (eventUser.role?.name) {
          if (!apiEventUsersGroups[eventUser.role.name]) {
            apiEventUsersGroups[eventUser.role?.name] = {
              role: eventUser.role,
              users: []
            };
          }
          apiEventUsersGroups[eventUser.role.name].users.push(eventUser);
        }
      });
      apiEventUsersGroups = Object.values(apiEventUsersGroups);
    }

    if (event?.ticketing?.useTicketing && registerParam) {
      handleRegisterClick();
    }

    setEventUsers(apiEventUsersGroups);
    // eslint-disable-next-line
  }, []);

  const RegisterButton = (props) => {
    return (
      <Button
        color="primary"
        variant="outlined"
        fullWidth
        sx={{
          color: "primary.contrastText",
          borderColor: "primary.contrastText",
          backgroundColor: "primary.main",

          "&:hover": {
            color: "primary.main",
            borderColor: "primary.main",
            backgroundColor: "primary.contrastText"
          }
        }}
        onClick={handleRegisterClick}
      >
        {t("registerForThisEvent")}
      </Button>
    );
  };

  const leavePrompt = async (url) => {
    try {
      await confirm({
        title: `You are now leaving ${getEnvironmentVariable("SITE_NAME")}`,
        description: `Click the button below to continue to ${url}`,
        dialogProps: {
          sx: {
            zIndex: 99999
          }
        },
        confirmationButtonProps: {
          variant: "contained",
          color: "info"
        },
        confirmationText: `Leave site`
      });
      setLeavingModalOpen(true);
    } catch (err) {
      return;
    }
  };

  const renderRegisterButton =
    event?.ticketing?.useTicketing &&
    !event?.status?.ended &&
    !event?.status?.started &&
    Boolean(event?.ticketing?.numTickets) &&
    event?.ticketing?.numTickets > 0 &&
    !event?.ticketing?.isFull &&
    moment().isSameOrBefore(event?.dates?.end, "day");

  const renderTicketPrice =
    Boolean(event?.ticketing?.numTickets) &&
    event?.ticketing?.numTickets > 0 &&
    event?.ticketing?.price > 0;

  const renderSoldOut =
    event?.ticketing?.useTicketing &&
    !event?.status?.started &&
    !event?.status?.ended &&
    event?.ticketing?.numTickets > 0 &&
    event?.ticketing?.isFull;

  const renderCheckin = !Boolean(
    event?.flags?.disableCheckin ||
      event?.status?.ended ||
      !event?.player ||
      !Boolean(
        !event?.player?.dropped &&
          !event?.player?.checkedIn &&
          (!event?.status?.started ||
            (event?.format?.leagueEvent && !event?.status?.ended)) &&
          moment().isAfter(moment(event?.dates?.start).subtract(1, "day"))
      )
  );

  const renderExternalUrl =
    Boolean(event?.externalUrl) && event?.externalUrl !== "";

  if (showLogin) {
    return <Login />;
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "banner.main"
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100%",
              minWidth: "100%",
              paddingTop: "0px !important"
            }}
          >
            <ImagePreview
              name={event?.name}
              imageFileId={event?.eventImage?.fileId}
              imagePhotoUrl={event?.eventImage?.photoUrl}
              event={event}
            />
          </Box>
          {appMode && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                position: "absolute",
                right: "5px",
                top: "50px"
              }}
            >
              <IconButton size="medium" color="inherit" onClick={followEvent}>
                <BCPStar outlined={!following} />
              </IconButton>
              <IconButton
                size="medium"
                color="inherit"
                onClick={() => setShareModalOpen(true)}
              >
                <BCPShareNodes
                  style={{ "font-style": "20px", width: "20px" }}
                />
              </IconButton>
            </Box>
          )}
        </Container>
      </Box>
      <Container maxWidth="lg">
        {loading && <ModalLoadingFull />}

        <Grid container sx={{ gap: 2 }}>
          <Grid item xs={12}>
            <Grid container sx={{ gap: 2 }}>
              <Hidden mdUp>
                <Grid item xs={12} sx={{ p: 1 }}>
                  <Grid container sx={{ gap: 2 }}>
                    {renderRegisterButton && (
                      <Grid item xs={12}>
                        <RegisterButton setShowLogin={setShowLogin} />
                      </Grid>
                    )}
                    {renderSoldOut && (
                      <Grid item xs={12}>
                        <Typography
                          variant="h4"
                          sx={{ textAlign: "center", fontWeight: "700" }}
                        >
                          Sold Out
                        </Typography>
                      </Grid>
                    )}

                    {renderCheckin && (
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          fullWidth
                          color={
                            event?.flags?.listsAtCheckin &&
                            !event?.player?.armyListObjectId
                              ? "warning"
                              : "success"
                          }
                          onClick={async (e) => {
                            try {
                              var errors = 0;
                              if (
                                event?.gameSystem?.hasFactions &&
                                !event?.player?.armyId
                              ) {
                                postNotification(
                                  "error",
                                  `You must select a ${
                                    event?.gameSystem?.factionLabel || "Faction"
                                  } prior to checking in`
                                );
                                errors++;
                              }
                              if (
                                event?.flags?.listsAtCheckin &&
                                !event?.player?.armyListObjectId
                              ) {
                                postNotification(
                                  "error",
                                  `This tournament requires players to submit a ${
                                    event?.gameSystem?.listLabel || "list"
                                  } prior to check in`
                                );
                                errors++;
                              }
                              if (errors > 0) {
                                handleTabChange(
                                  null,
                                  tabIndexes["player-details"]
                                );
                                return;
                              }
                              setLoading(true);

                              var updatePlayer = await postData(
                                `/players/${event?.player?.id}`,
                                {
                                  body: {
                                    set: { checkedIn: true }
                                  }
                                }
                              );
                              setEvent({
                                ...event,
                                player: {
                                  ...event?.player,
                                  ...updatePlayer
                                }
                              });
                            } catch (err) {
                              postNotification("error", err);
                            }

                            setLoading(false);
                          }}
                        >
                          Check In
                        </Button>
                      </Grid>
                    )}
                    {renderExternalUrl && (
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={() => {
                            leavePrompt(event?.externalUrl);
                          }}
                        >
                          External Link
                        </Button>
                      </Grid>
                    )}
                    {getEnvironmentVariable("COMPANY_FLAG") === "solforge" &&
                      event?.format?.eventSubType && (
                        <Grid item xs={12}>
                          <CustomChip
                            label={`${event?.format?.eventSubType} Event`}
                            color={event?.format?.eventSubType.toLowerCase()}
                          />
                        </Grid>
                      )}

                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {event?.gameSystem?.name}
                      </Typography>

                      <Typography variant="h4" gutterBottom>
                        {event?.name}
                      </Typography>

                      {event?.format?.eventFormat && (
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {event?.format?.eventFormat}
                        </Typography>
                      )}

                      {event?.format?.leagueEvent && (
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          League Event
                        </Typography>
                      )}

                      {getEnvironmentVariable("COMPANY_FLAG") !== "solforge" &&
                        event?.format?.eventSubType && (
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                          >
                            {event?.format?.eventSubType}
                          </Typography>
                        )}

                      {(event?.format?.doublesEvent ||
                        event?.format?.teamEvent) && (
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {event?.format?.doublesEvent
                            ? t("doublesEvent")
                            : t("teamEvent")}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("eventStarts")}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {moment(event?.dates?.start).format("LL")}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {moment(event?.dates?.start).format("LT")}{" "}
                        {t("yourLocalTime")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("eventEnd")}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {moment(event?.dates?.end).format("LL")}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {moment(event?.dates?.end).format("LT")}{" "}
                        {t("yourLocalTime")}
                      </Typography>
                    </Grid>
                    {Boolean(event?.gameStore?.name || event?.location) && (
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t("location")}
                        </Typography>

                        {Boolean(event?.gameStore?.name) && (
                          <Typography variant="h5" gutterBottom>
                            <Link
                              underline="none"
                              color="link.main"
                              href={`/organization/${event?.gameStore?.id}`}
                            >
                              {`${event?.gameStore.name}`.trim()}
                            </Link>
                          </Typography>
                        )}

                        <Link
                          underline="none"
                          color="link.main"
                          href={`https://google.com/maps/place/${
                            event?.location?.streetNum || ""
                          } ${event?.location?.streetName || ""} ${
                            event?.location?.subPremise || ""
                          } ${event?.location?.city || ""} ${event?.location?.zip || ""} ${
                            event?.location?.state || ""
                          } ${event?.location?.country || ""}`.trim()}
                          target="_blank"
                        >
                          {Boolean(
                            event?.location?.streetNum ||
                              event?.location?.streetName
                          ) && (
                            <Typography>
                              {`${event?.location?.streetNum || ""} ${
                                event?.location?.streetName || ""
                              }`.trim()}
                            </Typography>
                          )}
                          {Boolean(event?.location?.subPremise) && (
                            <Typography>
                              {`${event?.location?.subPremise || ""}`.trim()}
                            </Typography>
                          )}
                          {Boolean(
                            event?.location?.city ||
                              event?.location?.zip ||
                              event?.location?.state
                          ) && (
                            <Typography>
                              {`${
                                event?.location?.city
                                  ? `${event?.location?.city}, `
                                  : ""
                              }${event?.location?.state || ""} ${
                                event?.location?.zip || ""
                              }`.trim()}
                            </Typography>
                          )}
                          {Boolean(event?.location?.country) && (
                            <Typography>{event?.location?.country}</Typography>
                          )}
                        </Link>
                      </Grid>
                    )}
                    {Boolean(event?.ticketing?.numTickets) &&
                      event?.ticketing?.numTickets > 0 && (
                        <>
                          {event?.ticketing?.price > 0 && (
                            <Grid item xs={12}>
                              <Typography
                                variant="h6"
                                color="text.secondary"
                                gutterBottom
                              >
                                {t("ticketPrice")}
                              </Typography>
                              <Typography variant="h5" gutterBottom>
                                {`${getCurrencySymbol(
                                  event?.ticketing?.fees?.Currency
                                )}${(event.ticketing.price / 100).toFixed(
                                  2
                                )} ${event?.ticketing?.fees?.Currency.toUpperCase()}`.trim()}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}

                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("eventOwner")}
                      </Typography>
                      {Boolean(event?.gameStore?.name) && (
                        <Typography variant="h5" gutterBottom>
                          <Link
                            underline="none"
                            color="link.main"
                            onClick={(e) => {
                              setContactTOModal("store");
                            }}
                            sx={{ cursor: "pointer" }}
                          >
                            {`${event?.gameStore.name}`.trim()}
                          </Link>
                        </Typography>
                      )}
                      <Typography variant="h5" gutterBottom>
                        <Link
                          underline="none"
                          color="link.main"
                          onClick={(e) => {
                            setContactTOModal("to");
                          }}
                          sx={{ cursor: "pointer" }}
                        >
                          {event?.owner?.firstName || event?.owner?.lastName
                            ? userDisplayName(
                                event?.owner?.firstName,
                                event?.owner?.lastName
                              )
                            : t("contactOrganizer")}
                        </Link>
                      </Typography>
                    </Grid>

                    {eventUsers?.length > 0 &&
                      eventUsers.map((eventUserGroup, index) => {
                        return (
                          <Grid key={index} item xs={12}>
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              gutterBottom
                            >
                              {`${eventUserGroup.role?.name}${
                                eventUserGroup.users?.length > 1 ? "s" : ""
                              }`}
                            </Typography>
                            {eventUserGroup.users.map((eventUser, i) => {
                              return (
                                <Typography key={i} variant="h5" gutterBottom>
                                  {userDisplayName(
                                    eventUser.firstName,
                                    eventUser.lastName
                                  )}
                                </Typography>
                              );
                            })}
                          </Grid>
                        );
                      })}

                    {!event?.flags?.hidePlayerCount && (
                      <>
                        <Grid item xs={12}>
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                          >
                            {event?.countLabel === "Tickets Remaining"
                              ? t("ticketsRemaining")
                              : event?.countLabel === "Total Players Registered"
                                ? t("totalPlayersRegistered")
                                : event?.countLabel}
                          </Typography>
                          <Typography variant="h5" gutterBottom>
                            {event?.countString}
                          </Typography>
                        </Grid>

                        {(event?.format?.teamEvent ||
                          event?.format?.doublesEvent) && (
                          <Grid item xs={12}>
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              gutterBottom
                            >
                              {t("numberOfPlayers")}
                            </Typography>
                            <Typography variant="h5" gutterBottom>
                              {event?.playerCounts.total || 0}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}

                    {event?.leagues?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t("circuits")}
                        </Typography>
                        {event?.leagues.map((league) => (
                          <Typography variant="h5" gutterBottom>
                            <Link
                              underline="none"
                              color="link.main"
                              href={`/rankings/${league?.id}`}
                            >
                              {`${league?.name}`.trim()}
                            </Link>
                          </Typography>
                        ))}
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("eventDetails")}
                      </Typography>

                      <Box
                        sx={{
                          // Container styling
                          width: "100%",
                          maxWidth: "100%",
                          overflow: "hidden",

                          "& pre": {
                            whiteSpace: "pre-wrap",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            width: "100%",
                            maxWidth: "100%"
                          },

                          "& code": {
                            whiteSpace: "pre-wrap",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            width: "100%",
                            maxWidth: "100%",
                            display: "inline-block"
                          },

                          "& p": {
                            marginBottom: 2,
                            maxWidth: "100%",
                            wordWrap: "break-word",
                            overflowWrap: "break-word"
                          }
                        }}
                      >
                        {event?.description && event?.description !== "" ? (
                          <MarkdownViewer source={event?.description} />
                        ) : event?.eventDescription &&
                          event?.eventDescription !== "" ? (
                          <>
                            {event?.eventDescription
                              .split("\n")
                              .map((i, key) => {
                                return (
                                  <Typography
                                    key={key}
                                    variant="body1"
                                    gutterBottom
                                    paragraph
                                    sx={{ wordWrap: "break-word" }}
                                  >
                                    {i}
                                  </Typography>
                                );
                              })}
                          </>
                        ) : null}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Hidden>

              <Hidden mdDown>
                <Grid item xs={8} sx={{ p: 1 }}>
                  <Grid container spacing={1}>
                    {getEnvironmentVariable("COMPANY_FLAG") === "solforge" &&
                      event?.format?.eventSubType && (
                        <Grid item xs={12}>
                          <CustomChip
                            label={`${event?.format?.eventSubType} Event`}
                            color={event?.format?.eventSubType.toLowerCase()}
                          />
                        </Grid>
                      )}

                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {event?.gameSystem?.name}
                      </Typography>

                      <Typography variant="h4" gutterBottom>
                        {event?.name}
                      </Typography>

                      {event?.format?.eventFormat && (
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {event?.format?.eventFormat}
                        </Typography>
                      )}

                      {event?.format?.leagueEvent && (
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t("leagueEvent")}
                        </Typography>
                      )}

                      {getEnvironmentVariable("COMPANY_FLAG") !== "solforge" &&
                        event?.format?.eventSubType && (
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                          >
                            {event?.format?.eventSubType}
                          </Typography>
                        )}

                      {(event?.format?.doublesEvent ||
                        event?.format?.teamEvent) && (
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {event?.format?.doublesEvent
                            ? t("doublesEvent")
                            : t("teamEvent")}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("eventStarts")}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {moment(event?.dates?.start).format("LL")}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {moment(event?.dates?.start).format("LT")}{" "}
                        {t("yourLocalTime")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("eventEnd")}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {moment(event?.dates?.end).format("LL")}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {moment(event?.dates?.end).format("LT")}{" "}
                        {t("yourLocalTime")}
                      </Typography>
                    </Grid>
                    {Boolean(event?.gameStore?.name || event?.location) && (
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t("location")}
                        </Typography>

                        {Boolean(event?.gameStore?.name) && (
                          <Typography variant="h5" gutterBottom>
                            <Link
                              underline="none"
                              color="link.main"
                              href={`/organization/${event?.gameStore?.id}`}
                            >
                              {`${event?.gameStore.name}`.trim()}
                            </Link>
                          </Typography>
                        )}

                        <Link
                          underline="none"
                          color="link.main"
                          href={`https://google.com/maps/place/${
                            event?.location?.streetNum || ""
                          } ${event?.location?.streetName || ""} ${
                            event?.location?.subPremise || ""
                          } ${event?.location?.city || ""} ${event?.location?.zip || ""} ${
                            event?.location?.state || ""
                          } ${event?.location?.country || ""}`.trim()}
                          target="_blank"
                        >
                          {Boolean(
                            event?.location?.streetNum ||
                              event?.location?.streetName
                          ) && (
                            <Typography>
                              {`${event?.location?.streetNum || ""} ${
                                event?.location?.streetName || ""
                              }`.trim()}
                            </Typography>
                          )}
                          {Boolean(event?.location?.subPremise) && (
                            <Typography>
                              {`${event?.location?.subPremise || ""}`.trim()}
                            </Typography>
                          )}
                          {Boolean(
                            event?.location?.city ||
                              event?.location?.zip ||
                              event?.location?.state
                          ) && (
                            <Typography>
                              {`${
                                event?.location?.city
                                  ? `${event?.location?.city}, `
                                  : ""
                              }${event?.location?.state || ""} ${
                                event?.location?.zip || ""
                              }`.trim()}
                            </Typography>
                          )}
                          {Boolean(event?.location?.country) && (
                            <Typography>{event?.location?.country}</Typography>
                          )}
                        </Link>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("eventOwner")}
                      </Typography>
                      {Boolean(event?.gameStore?.name) && (
                        <Typography variant="h5" gutterBottom>
                          <Link
                            underline="none"
                            color="link.main"
                            href={`/organization/${event?.gameStore?.id}`}
                            sx={{ cursor: "pointer" }}
                          >
                            {`${event?.gameStore.name}`.trim()}
                          </Link>
                        </Typography>
                      )}
                      <Typography variant="h5" gutterBottom>
                        <Link
                          underline="none"
                          color="link.main"
                          onClick={(e) => {
                            setContactTOModal("to");
                          }}
                          sx={{ cursor: "pointer" }}
                        >
                          {event?.owner?.firstName || event?.owner?.lastName
                            ? userDisplayName(
                                event?.owner?.firstName,
                                event?.owner?.lastName
                              )
                            : t("contactOrganizer")}
                        </Link>
                      </Typography>
                    </Grid>

                    {eventUsers?.length > 0 &&
                      eventUsers.map((eventUserGroup, index) => {
                        return (
                          <Grid key={index} item xs={12}>
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              gutterBottom
                            >
                              {`${eventUserGroup.role?.name}${
                                eventUserGroup.users?.length > 1 ? "s" : ""
                              }`}
                            </Typography>
                            {eventUserGroup.users.map((eventUser, i) => {
                              return (
                                <Typography key={i} variant="h5" gutterBottom>
                                  {userDisplayName(
                                    eventUser.firstName,
                                    eventUser.lastName
                                  )}
                                </Typography>
                              );
                            })}
                          </Grid>
                        );
                      })}

                    {!event?.flags?.hidePlayerCount && (
                      <>
                        <Grid item xs={12}>
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                          >
                            {event?.countLabel === "Tickets Remaining"
                              ? t("ticketsRemaining")
                              : event?.countLabel === "Total Players Registered"
                                ? t("totalPlayersRegistered")
                                : event?.countLabel}
                          </Typography>
                          <Typography variant="h5" gutterBottom>
                            {event?.countString}
                          </Typography>
                        </Grid>

                        {(event?.format?.teamEvent ||
                          event?.format?.doublesEvent) && (
                          <Grid item xs={12}>
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              gutterBottom
                            >
                              {t("numberOfPlayers")}
                            </Typography>
                            <Typography variant="h5" gutterBottom>
                              {event?.playerCounts.total || 0}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}

                    {event?.leagues?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t("circuits")}
                        </Typography>
                        {event?.leagues.map((league) => (
                          <Typography variant="h5" gutterBottom>
                            <Link
                              underline="none"
                              color="link.main"
                              href={`/rankings/${league?.id}`}
                            >
                              {`${league?.name}`.trim()}
                            </Link>
                          </Typography>
                        ))}
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("eventDetails")}
                      </Typography>

                      <Box
                        sx={{
                          // Container styling
                          width: "100%",
                          maxWidth: "100%",
                          overflow: "hidden",

                          "& pre": {
                            whiteSpace: "pre-wrap",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            width: "100%",
                            maxWidth: "100%"
                          },

                          "& code": {
                            whiteSpace: "pre-wrap",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            width: "100%",
                            maxWidth: "100%",
                            display: "inline-block"
                          },

                          "& p": {
                            marginBottom: 2,
                            maxWidth: "100%",
                            wordWrap: "break-word",
                            overflowWrap: "break-word"
                          }
                        }}
                      >
                        {event?.description && event?.description !== "" ? (
                          <MarkdownViewer source={event?.description} />
                        ) : event?.eventDescription &&
                          event?.eventDescription !== "" ? (
                          <>
                            {event?.eventDescription
                              .split("\n")
                              .map((i, key) => {
                                return (
                                  <Typography
                                    key={key}
                                    variant="body1"
                                    gutterBottom
                                    paragraph
                                    sx={{ wordWrap: "break-word" }}
                                  >
                                    {i}
                                  </Typography>
                                );
                              })}
                          </>
                        ) : null}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
                {(renderTicketPrice ||
                  renderRegisterButton ||
                  renderExternalUrl ||
                  renderSoldOut ||
                  renderCheckin) && (
                  <Grid
                    item
                    xs={4}
                    sx={{
                      p: 1,
                      maxWidth: "calc(33.333333% - 16px) !important",
                      minWidth: "calc(33.333333% - 16px) !important"
                    }}
                  >
                    <Grid
                      ref={rightBoxRef}
                      container
                      spacing={1}
                      sx={{
                        mt: 1,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "primary.main",
                        boxShadow: null
                      }}
                    >
                      {renderTicketPrice && (
                        <Grid item xs={12} textAlign="center">
                          <Typography
                            variant="h6"
                            color="primary.contrastText"
                            gutterBottom
                          >
                            {t("ticketPrice")}
                          </Typography>
                          <Typography
                            variant="h5"
                            gutterBottom
                            color="primary.contrastText"
                          >
                            {`${getCurrencySymbol(
                              event?.ticketing?.fees?.Currency
                            )}${(event.ticketing.price / 100).toFixed(
                              2
                            )} ${event?.ticketing?.fees?.Currency.toUpperCase()}`.trim()}
                          </Typography>
                        </Grid>
                      )}
                      {renderRegisterButton && (
                        <Grid item xs={12}>
                          <RegisterButton />
                        </Grid>
                      )}
                      {renderSoldOut && (
                        <Grid item xs={12}>
                          <Typography
                            variant="h4"
                            sx={{ textAlign: "center", fontWeight: "700" }}
                          >
                            {t("soldOut")}
                          </Typography>
                        </Grid>
                      )}
                      {renderCheckin && (
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            fullWidth
                            color={
                              event?.flags?.listsAtCheckin &&
                              !event?.player?.armyListObjectId
                                ? "warning"
                                : "success"
                            }
                            onClick={async (e) => {
                              try {
                                var errors = 0;
                                if (
                                  event?.gameSystem?.hasFactions &&
                                  !event?.player?.armyId
                                ) {
                                  postNotification(
                                    "error",
                                    `You must select a ${
                                      event?.gameSystem?.factionLabel ||
                                      "Faction"
                                    } prior to checking in`
                                  );
                                  errors++;
                                }
                                if (
                                  event?.flags?.listsAtCheckin &&
                                  !event?.player?.armyListObjectId
                                ) {
                                  postNotification(
                                    "error",
                                    `This tournament requires players to submit a ${
                                      event?.gameSystem?.listLabel || "list"
                                    } prior to check in`
                                  );
                                  errors++;
                                }
                                if (errors > 0) {
                                  handleTabChange(
                                    null,
                                    tabIndexes["player-details"]
                                  );
                                  return;
                                }
                                setLoading(true);

                                var updatePlayer = await postData(
                                  `/players/${event?.player?.id}`,
                                  {
                                    body: {
                                      set: { checkedIn: true }
                                    }
                                  }
                                );
                                setEvent({
                                  ...event,
                                  player: {
                                    ...event?.player,
                                    ...updatePlayer
                                  }
                                });
                              } catch (err) {
                                postNotification("error", err);
                              }

                              setLoading(false);
                            }}
                          >
                            Check In
                          </Button>
                        </Grid>
                      )}
                      {renderExternalUrl && (
                        <Grid item xs={12}>
                          <Button
                            variant={
                              theme?.palette?.mode === "light"
                                ? "contained"
                                : "outlined"
                            }
                            color={
                              theme?.palette?.mode === "light"
                                ? "secondary"
                                : "primary"
                            }
                            fullWidth
                            onClick={() => {
                              leavePrompt(event?.externalUrl);
                            }}
                          >
                            External Link
                          </Button>
                        </Grid>
                      )}
                    </Grid>

                    {!rightBoxInView && (
                      <Box
                        sx={{
                          position: "fixed",
                          zIndex: 999,
                          p: 2,
                          bgcolor: "primary.main",
                          borderRadius: 1,
                          boxShadow: 3,
                          top: ["56px", "64px", "64px"],
                          left: [
                            "calc( (100vw / 12 * 8) - 8px)",
                            "calc( (100vw / 12 * 8) - 8px)",
                            "calc( (100vw / 12 * 8) - 8px)",
                            "calc( ( (100vw - 1200px) / 2) + (1200px / 12 * 8) - 8px)"
                          ],
                          width: [
                            "calc(100vw / 12 * 4 - 32px)",
                            "calc(100vw / 12 * 4 - 32px)",
                            "calc(100vw / 12 * 4 - 32px)",
                            "calc(1200px / 12 * 4 - 32px)"
                          ]
                        }}
                      >
                        <Grid container spacing={1}>
                          {Boolean(event?.ticketing?.numTickets) &&
                            event?.ticketing?.numTickets > 0 && (
                              <>
                                {event?.ticketing?.price > 0 && (
                                  <Grid item xs={12} textAlign="center">
                                    <Typography
                                      variant="h6"
                                      color="primary.contrastText"
                                      gutterBottom
                                    >
                                      {t("ticketPrice")}
                                    </Typography>
                                    <Typography
                                      variant="h5"
                                      gutterBottom
                                      color="primary.contrastText"
                                    >
                                      {`${getCurrencySymbol(
                                        event?.ticketing?.fees?.Currency
                                      )}${(event.ticketing.price / 100).toFixed(
                                        2
                                      )} ${event?.ticketing?.fees?.Currency.toUpperCase()}`.trim()}
                                    </Typography>
                                  </Grid>
                                )}
                              </>
                            )}
                          {renderRegisterButton && (
                            <Grid item xs={12}>
                              <RegisterButton />
                            </Grid>
                          )}
                          {renderSoldOut && (
                            <Grid item xs={12}>
                              <Typography
                                variant="h4"
                                sx={{ textAlign: "center", fontWeight: "700" }}
                              >
                                {t("soldOut")}
                              </Typography>
                            </Grid>
                          )}
                          {renderCheckin && (
                            <Grid item xs={12}>
                              <Button
                                variant="contained"
                                fullWidth
                                color={
                                  event?.flags?.listsAtCheckin &&
                                  !event?.player?.armyListObjectId
                                    ? "warning"
                                    : "success"
                                }
                                onClick={async (e) => {
                                  try {
                                    var errors = 0;
                                    if (
                                      event?.gameSystem?.hasFactions &&
                                      !event?.player?.armyId
                                    ) {
                                      postNotification(
                                        "error",
                                        `You must select a ${
                                          event?.gameSystem?.factionLabel ||
                                          "Faction"
                                        } prior to checking in`
                                      );
                                      errors++;
                                    }
                                    if (
                                      event?.flags?.listsAtCheckin &&
                                      !event?.player?.armyListObjectId
                                    ) {
                                      postNotification(
                                        "error",
                                        `This tournament requires players to submit a ${
                                          event?.gameSystem?.listLabel || "list"
                                        } prior to check in`
                                      );
                                      errors++;
                                    }
                                    if (errors > 0) {
                                      handleTabChange(
                                        null,
                                        tabIndexes["player-details"]
                                      );
                                      return;
                                    }
                                    setLoading(true);

                                    var updatePlayer = await postData(
                                      `/players/${event?.player?.id}`,
                                      {
                                        body: {
                                          set: { checkedIn: true }
                                        }
                                      }
                                    );
                                    setEvent({
                                      ...event,
                                      player: {
                                        ...event?.player,
                                        ...updatePlayer
                                      }
                                    });
                                  } catch (err) {
                                    postNotification("error", err);
                                  }

                                  setLoading(false);
                                }}
                              >
                                Check In
                              </Button>
                            </Grid>
                          )}
                          {renderExternalUrl && (
                            <Grid item xs={12}>
                              <Button
                                variant={
                                  theme?.palette?.mode === "light"
                                    ? "contained"
                                    : "outlined"
                                }
                                color={
                                  theme?.palette?.mode === "light"
                                    ? "secondary"
                                    : "primary"
                                }
                                fullWidth
                                onClick={() => {
                                  leavePrompt(event?.externalUrl);
                                }}
                              >
                                External Link
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}
                  </Grid>
                )}
              </Hidden>
            </Grid>
          </Grid>
        </Grid>

        {Boolean(ticketModal) && (
          <TicketModal
            ticketModal={ticketModal}
            setTicketModal={setTicketModal}
            authContext={authContext}
            event={event}
            refreshEvent={refreshEvent}
            tGeneral={tGeneral}
          />
        )}
        {Boolean(contactTOModal) && (
          <ContactTOModal
            open={contactTOModal}
            setOpen={setContactTOModal}
            authContext={authContext}
            event={event}
            tGeneral={tGeneral}
          />
        )}
        {myWarhammerPrompt && (
          <MyWarhammerPromptModal
            open={myWarhammerPrompt}
            setOpen={setMyWarhammerPrompt}
            event={event}
          />
        )}

        <ShareModal
          parentOpen={shareModalOpen}
          setParentOpen={setShareModalOpen}
          url={`${window.location.origin}/event/${event?.id}`}
          shareOptions={{
            twitter: `http://twitter.com/share?text=Checkout ${event?.name}${
              getEnvironmentVariable("COMPANY_NAME") === "Best Coast Pairings"
                ? " @BCPairings"
                : ""
            }&url=${window.location.origin}/event/${
              event?.id
            }&hashtags=${getEnvironmentVariable("COMPANY_NAME")
              .split(" ")
              .join("")},${event?.gameSystem?.name.split(" ").join("")}`,
            email: `mailto:?subject=Checkout ${
              event?.name
            } at ${getEnvironmentVariable("COMPANY_NAME")}&body=Checkout ${
              event?.name
            } at ${getEnvironmentVariable("COMPANY_NAME")} ${
              window.location.origin
            }/event/${event?.id}`
          }}
        />
        {leavingModalOpen && (
          <RedirectModal
            url={event?.externalUrl}
            open={leavingModalOpen}
            setOpen={setLeavingModalOpen}
          />
        )}
      </Container>
    </>
  );
}

function MyWarhammerPromptModal(props) {
  const { open, setOpen, event } = props;
  const gameSystemStartsWithVowel = wordStartsWithVowel(
    event?.gameSystem?.name
  );
  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => setOpen(false)}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          maxHeight: "95vh",
          overflowY: "scroll",
          bgcolor: "modal.background",
          color: "modal.text",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => setOpen(false)}
        >
          <BCPXmark />
        </IconButton>
        <Grid
          container
          spacing={1}
          sx={{
            mb: 1,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
          }}
        >
          <Grid item xs={12}>
            To register for {gameSystemStartsWithVowel ? "an" : "a"}{" "}
            {event?.gameSystem?.name} event you require a MyWarhammer account
            linked to your BCP account. To do so, please go to the{" "}
            <Link href="/account?active_tab=identities">Identities</Link> tab on
            your BCP account and press the Link with MyWarhammer button.
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

export function TicketModal(props) {
  const { ticketModal, setTicketModal, event, authContext, tGeneral } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "components.playerRegistration"
  });
  const currency =
    Object.keys(event?.pricingDict || {})?.[0] ||
    event?.ticketing?.currency ||
    "usd";
  const ticketPrice =
    (event?.pricingDict?.[currency] ||
      event?.ticketPrice ||
      event?.ticketing?.price) / 100;

  const [teams, setTeams] = useState(null);

  const [playerData, setPlayerData] = useState({});
  const [cancel, setCancel] = useState(false);
  const [preLoading, setPreLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [transactionQueued, setTransactionQueued] = useState(null);

  const [clientSecret, setClientSecret] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [transactionOrderId, setTransactionOrderId] = useState("");

  const [ticketingFeeSummary, setTicketingFeeSummary] = useState(null);

  const handleSetTeams = async (userId) => {
    if (userId) {
      try {
        const apiTeams = await handlePagingAll("/teams", {
          userId: userId,
          "expand[]": ["owner"]
        });

        let newTeams = [];
        if (apiTeams && apiTeams?.length) {
          apiTeams.sort((a, b) => {
            let teamA = a?.name?.toLowerCase();
            let teamB = b?.name?.toLowerCase();

            if (teamA > teamB) return 1;
            if (teamA < teamB) return -1;

            return 0;
          });

          apiTeams.forEach((team) =>
            newTeams.push({ label: team?.name, value: team?.id })
          );

          setTeams(newTeams);
        } else {
          setTeams(null);
        }
      } catch (err) {}
    } else {
      setTeams(null);
    }
  };

  useEffect(() => {
    let ticketingDict = getTicketing();
    let _ticketingFeeSummary = getTicketingFeeSummary(
      1,
      ticketPrice,
      event?.ticketing?.playerPaysFees &&
        ticketingDict[currency?.toUpperCase()]?.FixedPlayer !== undefined
        ? ticketingDict[currency?.toUpperCase()]?.FixedPlayer
        : ticketingDict[currency?.toUpperCase()]?.Fixed,
      event?.ticketing?.playerPaysFees &&
        ticketingDict[currency?.toUpperCase()]?.PercentPlayer !== undefined
        ? ticketingDict[currency?.toUpperCase()]?.PercentPlayer
        : ticketingDict[currency?.toUpperCase()]?.Percent,
      !event?.ticketing?.playerPaysFees
    );

    setTicketingFeeSummary(_ticketingFeeSummary);

    const go = async () => {
      const initialPlayerData = {
        email: authContext?.authState?.identity?.email || "",
        firstName:
          authContext?.authState?.identity?.firstName ||
          authContext?.authState?.identity?.given_name ||
          "",
        lastName:
          authContext?.authState?.identity?.lastName ||
          authContext?.authState?.identity?.family_name ||
          "",
        teamName: "",
        teamId: "",
        accessCode: "",
        systemId: "",
        policy: false
      };

      if (authContext?.authState?.identity?.userId) {
        try {
          var apiUser = await getData(
            `/users/${authContext?.authState?.identity?.userId}`,
            {
              queryStringParameters: {
                "expand[]": ["team"]
              }
            }
          );
          if (apiUser?.team?.id) {
            initialPlayerData.teamId = apiUser?.team?.id;
          }
        } catch (err) {}

        try {
          await handleSetTeams(authContext?.authState?.identity?.userId);
        } catch (err) {}
      }
      setPlayerData(initialPlayerData);
      if (Boolean(ticketModal) && !transactionQueued) {
        if (event?.ticketing?.price > 0) {
          try {
            var apiTransactionQueued = await postData(
              `/transactions/preRegister`,
              {
                body: {
                  eventId: event?.id
                }
              }
            );

            setTransactionQueued(apiTransactionQueued);
            setPreLoading(false);

            await checkQueueStatus(apiTransactionQueued);
          } catch (err) {
            postNotification("error", err);
            cancelCheckout();
            return;
          }
        }
        setPreLoading(false);
      }
    };
    if (preLoading) go();
    // eslint-disable-next-line
  }, [props]);

  const delay = (time) => {
    return new Promise((resolve) => setTimeout(resolve, time));
  };

  const submitPlayer = async (_playerData) => {
    setLoading(true);
    var body = {
      eventId: event?.id,
      [event?.format?.teamEvent || event?.format?.doublesEvent
        ? "teamCaptain"
        : "user"]: {
        email: _playerData.email,
        firstName: _playerData.firstName,
        lastName: _playerData.lastName
      }
    };
    if (
      (event?.gameSystem?.usesSystemId ||
        event?.gameSystem?.systemIdRequired) &&
      !event?.format?.teamEvent &&
      !event?.format?.doublesEvent
    ) {
      body.systemId = _playerData.systemId;
      body.systemIdKey = event?.gameSystem?.systemIdName;
    }

    if (event?.format?.teamEvent || event?.format?.doublesEvent) {
      if (_playerData.teamName?.length > 0) {
        body.name = _playerData.teamName;
      }
    } else {
      if (_playerData.teamId?.length > 0) {
        body.teamId = _playerData.teamId;
      }
    }
    if (_playerData.accessCode?.length > 0) {
      body.accessCode = _playerData.accessCode;
    }
    if (Object.keys(_playerData.shippingDetails || {}).length > 0) {
      body.shippingDetails = _playerData.shippingDetails;
    }

    try {
      await postData(
        event?.format?.teamEvent || event?.format?.doublesEvent
          ? "/teamplayers"
          : "/players",
        {
          body: body
        }
      );

      postNotification(
        "success",
        tGeneral("notifications.success.registrationSuccessGeneric")
      );

      if (!authContext?.authState?.identity) {
        postNotification(
          "warning",
          `You must sign in to edit or view ${
            event?.format?.teamEvent || event?.format?.doublesEvent
              ? "team or"
              : ""
          } player details.`
        );
      }

      setTimeout(() => {
        window.location.assign(
          `/event/${event?.id}?active_tab=${
            event?.format?.teamEvent || event?.format?.doublesEvent
              ? "team-details"
              : "player-details"
          }`
        );
      }, [!authContext?.authState?.identity ? 5000 : 2000]);
    } catch (err) {
      postNotification("error", err);
      setLoading(false);
    }
  };

  const checkQueueStatus = async (intitalTransactionQueued) => {
    await delay(5000);
    if (cancel) {
      return;
    }
    if (!intitalTransactionQueued?.id) {
      console.error("Error: no transaction to check queue against");
      return;
    }
    try {
      var apiTransactionQueued = await getData(
        `/transactions/${intitalTransactionQueued.id}`
      );

      if (intitalTransactionQueued?.authorizeNetKeys) {
        apiTransactionQueued.authorizeNetKeys =
          intitalTransactionQueued.authorizeNetKeys;
      }

      setTransactionQueued(apiTransactionQueued);
      if (
        apiTransactionQueued.queuedPosition !== undefined &&
        apiTransactionQueued.queuedPosition !==
          intitalTransactionQueued.queuedPosition
      ) {
        postNotification(
          "info",
          tGeneral("notifications.info.queuePositionUpdated")
        );
      } else if (
        apiTransactionQueued.status !== undefined &&
        apiTransactionQueued.status !== intitalTransactionQueued.status
      ) {
        if (intitalTransactionQueued.status === "Queued") {
          if (apiTransactionQueued.status === "Pending Checkout") {
            postNotification(
              "info",
              "A ticket has come available, please complete the player registration."
            );
          }
        }
      }
      if (
        apiTransactionQueued.status === "Queued" ||
        apiTransactionQueued.status === "Pending Checkout" ||
        apiTransactionQueued.status === "Pending Charge"
      ) {
        await checkQueueStatus(apiTransactionQueued);
        return;
      }
      if (intitalTransactionQueued.status === "Expired Checkout") {
        postNotification(
          "warning",
          tGeneral("notifications.warnings.playerRegistrationExpired")
        );
      } else if (intitalTransactionQueued.status === "Event Full") {
        postNotification(
          "warning",
          tGeneral("notifications.warnings.noTicketsRemaining")
        );
      }

      setTransactionQueued(null);
      setTicketModal(null);
      return;
    } catch (err) {
      if (!cancel) {
        postNotification(
          "error",
          `Your ${
            event?.ticketing?.price > 0 ? "Purchase" : "Registration"
          } has been cancelled or expired.`
        );
      }
      setTransactionQueued(null);
      setTicketModal(null);
    }
  };

  const cancelCheckout = async () => {
    setCancel(true);
    if (transactionQueued?.id) {
      setLoading(true);
      try {
        await postData(`/transactions/${transactionQueued.id}/cancelCheckout`);
      } catch (err) {
        postNotification("error", err);
      }
    }
    setLoading(false);
    setTransactionQueued(null);
    setTicketModal(null);
    setPreLoading(true);
  };

  const PolicyComponent = () => {
    return (
      <>
        <Typography variant="body1" gutterBottom>
          {t("privacyDescription")}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {t("visitLinksFor")}{" "}
          <Link href="/privacy" target="_blank" underline="none">
            {t("privacy")}
          </Link>{" "}
          {t("policyAnd")}{" "}
          <Link href="/eula" target="_blank" underline="none">
            {t("eula")}
          </Link>
        </Typography>
        <Typography variant="body1">{t("contactOccasionally")}</Typography>
      </>
    );
  };

  return (
    <>
      <Modal
        disableAutoFocus
        open={Boolean(ticketModal)}
        onClose={cancel}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "modal.background",
            color: "modal.text",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={cancelCheckout}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={1}
            sx={{
              mb: 1,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                {event?.format?.teamEvent || event?.format?.doublesEvent
                  ? t("teamRegistration")
                  : t("playerRegistration")}{" "}
              </Typography>
              {transactionQueued?.queuedPosition === undefined &&
                event?.ticketing?.price > 0 && (
                  <>
                    <Typography variant="caption" gutterBottom>
                      {t("fiveMinutesToComplete", {
                        type:
                          event?.ticketing?.price > 0
                            ? t("purchase")
                            : t("registration")
                      })}
                    </Typography>
                    {transactionQueued?.status === "Pending Charge" ? (
                      <CountdownTimer
                        timerData={{
                          startTime: moment(transactionQueued?.updated_at)
                            .utc()
                            .toISOString(),
                          endTime: moment(transactionQueued?.updated_at)
                            .add(5, "minutes")
                            .utc()
                            .toISOString()
                        }}
                        animateMinutes={1}
                      />
                    ) : (
                      <CountdownTimer
                        timerData={{
                          startTime: moment(transactionQueued?.updated_at)
                            .utc()
                            .toISOString(),
                          endTime: moment(transactionQueued?.updated_at)
                            .add(5, "minutes")
                            .utc()
                            .toISOString()
                        }}
                        animateMinutes={1}
                      />
                    )}
                  </>
                )}
            </Grid>
          </Grid>
          <Box sx={{ minHeight: "400px" }}>
            {transactionQueued?.queuedPosition !== undefined && (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body1" gutterBottom>
                      {t("queueDescription", {
                        position: transactionQueued?.queuedPosition,
                        type:
                          event?.ticketing?.price > 0
                            ? t("purchase")
                            : t("registration")
                      })}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {t("yourQueuePosition")}
                    </Typography>
                    <Typography variant="h3" gutterBottom>
                      {transactionQueued?.queuedPosition === 0
                        ? t("next")
                        : transactionQueued?.queuedPosition}
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ color: "error.main" }}
                    >
                      {t("doNotCloseScreen")}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            {!preLoading && (
              <>
                <Formik
                  initialValues={playerData}
                  enableReinitialize
                  validationSchema={
                    event?.format?.teamEvent || event?.format?.doublesEvent
                      ? Yup.object().shape({
                          email: Yup.string()
                            .max(128)
                            .email(t("validEmailRequired"))
                            .required(t("required")),
                          firstName: Yup.string()
                            .max(128)
                            .required(t("required")),
                          lastName: Yup.string()
                            .max(128)
                            .required(t("required")),
                          teamName: Yup.string()
                            .max(128)
                            .required(t("required")),
                          policy:
                            event?.ticketing?.price > 0
                              ? null
                              : Yup.boolean().oneOf([true], "Required"),
                          accessCode: event?.ticketing?.privateEvent
                            ? Yup.string().max(255).required("Required")
                            : null
                        })
                      : Yup.object().shape({
                          email: Yup.string()
                            .max(128)
                            .email(t("validEmailRequired"))
                            .required(t("required")),
                          firstName: Yup.string()
                            .max(128)
                            .required(t("required")),
                          lastName: Yup.string()
                            .max(128)
                            .required(t("required")),
                          teamId: Yup.string(),
                          policy:
                            event?.ticketing?.price > 0
                              ? null
                              : Yup.boolean().oneOf([true], "Required"),
                          accessCode: event?.ticketing?.privateEvent
                            ? Yup.string().max(255).required("Required")
                            : null,
                          systemId: event?.gameSystem?.systemIdRequired
                            ? Yup.string().required(t("required"))
                            : Yup.string()
                        })
                  }
                  onSubmit={async (values) => {
                    if (
                      event?.ticketing?.price > 0 ||
                      event?.ticketing?.shippingDetails?.requested
                    ) {
                      setPlayerData(values);
                      setTicketModal(event?.ticketing?.price > 0 ? 2 : 3);
                    } else {
                      await submitPlayer(values);
                    }
                    return;
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                    setFieldValue
                  }) => (
                    <form onSubmit={handleSubmit}>
                      {transactionQueued?.queuedPosition === undefined &&
                        ticketModal === 1 && (
                          <Grid container spacing={1}>
                            {(event?.format?.teamEvent ||
                              event?.format?.doublesEvent) && (
                              <Grid item xs={12}>
                                <TextField
                                  name="teamName"
                                  label={t("teamName")}
                                  value={values.teamName}
                                  variant="outlined"
                                  fullWidth
                                  margin="dense"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  error={Boolean(
                                    touched.teamName && errors.teamName
                                  )}
                                  helperText={
                                    touched.teamName && errors.teamName
                                  }
                                  required
                                />
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <TextField
                                name="firstName"
                                label={`${
                                  event?.format?.teamEvent ||
                                  event?.format?.doublesEvent
                                    ? t("captainFirstName")
                                    : t("firstName")
                                }`}
                                value={values.firstName}
                                variant="outlined"
                                fullWidth
                                required
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.firstName && errors.firstName
                                )}
                                helperText={
                                  touched.firstName && errors.firstName
                                }
                                disabled={ENFORCE_ACCOUNT}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="lastName"
                                label={`${
                                  event?.format?.teamEvent ||
                                  event?.format?.doublesEvent
                                    ? t("captainLastName")
                                    : t("lastName")
                                }`}
                                value={values.lastName}
                                variant="outlined"
                                fullWidth
                                required
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.lastName && errors.lastName
                                )}
                                helperText={touched.lastName && errors.lastName}
                                disabled={ENFORCE_ACCOUNT}
                              />
                            </Grid>

                            {(event?.gameSystem?.usesSystemId ||
                              event?.gameSystem?.systemIdRequired) && (
                              <Grid item xs={12}>
                                <TextField
                                  name="systemId"
                                  label={
                                    event?.gameSystem?.systemIdName ||
                                    t("systemId")
                                  }
                                  value={values.systemId}
                                  variant="outlined"
                                  fullWidth
                                  required={event?.gameSystem?.systemIdRequired}
                                  margin="dense"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  error={Boolean(
                                    touched.systemId && errors.systemId
                                  )}
                                  helperText={
                                    touched.systemId && errors.systemId
                                  }
                                />
                              </Grid>
                            )}
                            {!SUPPORTS_UY_PLATFORM_FEATURES &&
                              !event?.format?.teamEvent &&
                              !event?.format?.doublesEvent && (
                                <Grid item xs={12}>
                                  <GenericPicker
                                    name="teamId"
                                    label={t("clubTeamName")}
                                    value={values.teamId}
                                    variant="outlined"
                                    fullWidth
                                    margin="dense"
                                    onBlur={handleBlur}
                                    handleChange={handleChange}
                                    error={Boolean(
                                      touched.teamId && errors.teamId
                                    )}
                                    helperText={touched.teamId && errors.teamId}
                                    list={teams || []}
                                    inclAny={false}
                                    clearable
                                    endAdornment={
                                      <InputAdornment position="end">
                                        <Button
                                          sx={{ minWidth: "40px" }}
                                          onClick={async (e) => {
                                            setLoading(true);
                                            await handleSetTeams(
                                              authContext?.authState?.identity
                                                ?.userId
                                            );
                                            setLoading(false);
                                          }}
                                        >
                                          <BCPRotateRight />
                                        </Button>
                                      </InputAdornment>
                                    }
                                  />
                                  <FormHelperText>
                                    {t("createJoinClubTeam")}{" "}
                                    <Link
                                      href="/account?active_tab=teams"
                                      target="_blank"
                                    >
                                      {t("account")}
                                    </Link>
                                  </FormHelperText>
                                </Grid>
                              )}
                            <Grid item xs={12}>
                              <TextField
                                name="email"
                                label={`${
                                  getEnvironmentVariable("APP_NAME") || "BCP"
                                } ${t("accountEmail", {
                                  type:
                                    event?.format?.teamEvent ||
                                    event?.format?.doublesEvent
                                      ? t("captain")
                                      : t("player")
                                })}`}
                                value={values.email}
                                variant="outlined"
                                fullWidth
                                required={event?.gameSystem?.emailRequired}
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(touched.email && errors.email)}
                                helperText={touched.email && errors.email}
                                disabled={ENFORCE_ACCOUNT}
                              />
                            </Grid>
                            {event?.ticketing?.privateEvent && (
                              <Grid item xs={12}>
                                <TextField
                                  name="accessCode"
                                  label={t("accessCode")}
                                  value={values.accessCode}
                                  variant="outlined"
                                  fullWidth
                                  margin="dense"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  error={Boolean(
                                    touched.accessCode && errors.accessCode
                                  )}
                                  helperText={
                                    touched.accessCode && errors.accessCode
                                  }
                                  required
                                />
                              </Grid>
                            )}
                            {!Boolean(event?.ticketing?.price > 0) && (
                              <>
                                <Grid item xs={12}>
                                  <PolicyComponent />
                                </Grid>
                                <Grid item xs={12}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        name="policy"
                                        checked={values.policy}
                                        defaultChecked={false}
                                        onChange={(e) => {
                                          setFieldValue(
                                            "policy",
                                            !values.policy
                                          );
                                        }}
                                      />
                                    }
                                    label={t("agreeToTerms")}
                                  />

                                  {Boolean(touched.policy && errors.policy) && (
                                    <FormHelperText error>
                                      {errors.policy}
                                    </FormHelperText>
                                  )}
                                </Grid>
                              </>
                            )}
                            <Grid item xs={6}>
                              <Button
                                color="error"
                                fullWidth
                                variant="outlined"
                                onClick={cancelCheckout}
                              >
                                {t("cancel")}
                              </Button>
                            </Grid>

                            <Grid item xs={6}>
                              <Button
                                color="success"
                                fullWidth
                                variant="contained"
                                type="submit"
                              >
                                {event?.ticketing?.price > 0
                                  ? t("next")
                                  : t("register")}
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                    </form>
                  )}
                </Formik>
                <Formik
                  initialValues={
                    paymentData || {
                      email:
                        authContext?.authState?.identity?.email ||
                        playerData?.email ||
                        "",
                      firstName:
                        authContext?.authState?.identity?.firstName ||
                        authContext?.authState?.identity?.given_name ||
                        playerData?.firstName ||
                        "",
                      lastName:
                        authContext?.authState?.identity?.lastName ||
                        authContext?.authState?.identity?.family_name ||
                        playerData?.lastName ||
                        "",
                      couponCode: "",
                      policy: false,
                      acknowledgeRefund: false
                    }
                  }
                  enableReinitialize
                  validationSchema={Yup.object().shape({
                    email: Yup.string()
                      .max(128)
                      .email(t("validEmailRequired"))
                      .required(t("required")),
                    firstName: Yup.string().max(128).required(t("required")),
                    lastName: Yup.string().max(128).required(t("required")),
                    couponCode: Yup.string().max(255),
                    policy: Yup.boolean().oneOf([true], t("required")),
                    acknowledgeRefund: event?.ticketing?.refundDate
                      ? Yup.boolean().oneOf([true], t("required"))
                      : Yup.boolean()
                  })}
                  onSubmit={async (values) => {
                    setPaymentData(values);
                    if (event?.ticketing?.shippingDetails?.requested) {
                      setTicketModal(3);
                    } else {
                      setLoading(true);
                      setTicketModal(4);
                    }
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                    setFieldValue
                  }) => (
                    <form onSubmit={handleSubmit}>
                      {transactionQueued?.queuedPosition === undefined &&
                        ticketModal === 2 && (
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography variant="h5" gutterBottom>
                                {t("billingInfo")}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="firstName"
                                label={t("firstName")}
                                value={values.firstName}
                                variant="outlined"
                                fullWidth
                                required
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.firstName && errors.firstName
                                )}
                                helperText={
                                  touched.firstName && errors.firstName
                                }
                                disabled={ENFORCE_ACCOUNT}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="lastName"
                                label={t("lastName")}
                                value={values.lastName}
                                variant="outlined"
                                fullWidth
                                required
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.lastName && errors.lastName
                                )}
                                helperText={touched.lastName && errors.lastName}
                                disabled={ENFORCE_ACCOUNT}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="email"
                                label={t("receiptEmail")}
                                value={values.email}
                                variant="outlined"
                                fullWidth
                                required
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  const trimmedString = e.target.value
                                    .trim()
                                    .toLowerCase();
                                  setFieldValue("email", trimmedString);
                                }}
                                error={Boolean(touched.email && errors.email)}
                                helperText={touched.email && errors.email}
                                disabled={ENFORCE_ACCOUNT}
                              />
                            </Grid>
                            {event?.ticketing?.chargeType !== "authorized" && (
                              <Grid item xs={12}>
                                <TextField
                                  name="couponCode"
                                  label={t("couponCode")}
                                  value={values.couponCode}
                                  variant="outlined"
                                  fullWidth
                                  margin="dense"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  error={Boolean(
                                    touched.couponCode && errors.couponCode
                                  )}
                                  helperText={
                                    touched.couponCode && errors.couponCode
                                  }
                                />
                              </Grid>
                            )}
                            {ticketingFeeSummary?.amount > 0 && (
                              <>
                                <Grid item xs={6}>
                                  <Typography paragraph>
                                    {t("ticketPrice")}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography paragraph>
                                    {numberWithCommas(
                                      ticketingFeeSummary?.total?.toFixed(2)
                                    )}{" "}
                                    {currency?.toUpperCase()}
                                  </Typography>
                                </Grid>
                                {!ticketingFeeSummary?.absorbFees && (
                                  <>
                                    <Grid item xs={6}>
                                      <Typography paragraph>
                                        {t("ticketFees")}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography paragraph>
                                        {numberWithCommas(
                                          ticketingFeeSummary?.fee?.toFixed(2)
                                        )}{" "}
                                        {currency?.toUpperCase()}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography paragraph>
                                        {t("totalAmount")}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography paragraph>
                                        {numberWithCommas(
                                          ticketingFeeSummary?.amount?.toFixed(
                                            2
                                          )
                                        )}{" "}
                                        {currency?.toUpperCase()}
                                      </Typography>
                                    </Grid>
                                  </>
                                )}
                              </>
                            )}
                            <Grid item xs={12}>
                              <PolicyComponent />
                            </Grid>
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name="policy"
                                    checked={values.policy}
                                    defaultChecked={false}
                                    onChange={(e) => {
                                      setFieldValue("policy", !values.policy);
                                    }}
                                  />
                                }
                                label={t("agreeToTerms")}
                              />

                              {Boolean(touched.policy && errors.policy) && (
                                <FormHelperText error>
                                  {errors.policy}
                                </FormHelperText>
                              )}
                            </Grid>

                            {event?.ticketing?.refundDate && (
                              <Grid item xs={12}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      name="acknowledgeRefund"
                                      checked={values.acknowledgeRefund}
                                      defaultChecked={false}
                                      onChange={(e) => {
                                        setFieldValue(
                                          "acknowledgeRefund",
                                          !values.acknowledgeRefund
                                        );
                                      }}
                                    />
                                  }
                                  label={t("acknowledgeRefundDate", {
                                    date: moment(
                                      event?.ticketing?.refundDate
                                    ).format("M.D.YYYY LT")
                                  })}
                                />

                                {Boolean(
                                  touched.acknowledgeRefund &&
                                    errors.acknowledgeRefund
                                ) && (
                                  <FormHelperText error>
                                    {errors.acknowledgeRefund}
                                  </FormHelperText>
                                )}
                              </Grid>
                            )}

                            <Grid item xs={6}>
                              <Button
                                color="error"
                                fullWidth
                                variant="outlined"
                                onClick={(e) => {
                                  setTicketModal(1);
                                }}
                              >
                                {t("back")}
                              </Button>
                            </Grid>
                            <Grid item xs={6}>
                              <Button
                                color="success"
                                fullWidth
                                variant="contained"
                                type="submit"
                              >
                                {t("next")}
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                    </form>
                  )}
                </Formik>
                <Formik
                  initialValues={{
                    firstName: playerData?.firstName || "",
                    lastName: playerData?.lastName || "",
                    email: playerData?.email || "",
                    company: "",
                    deliveryNotes: "",
                    address1: "",
                    address2: "",
                    city: "",
                    zip: "",
                    state: "",
                    country: ""
                  }}
                  enableReinitialize
                  validationSchema={Yup.object().shape({
                    firstName: event?.ticketing?.shippingDetails?.mandatory
                      ? Yup.string().max(128).required("Required")
                      : Yup.string().max(128),
                    lastName: event?.ticketing?.shippingDetails?.mandatory
                      ? Yup.string().max(128).required("Required")
                      : Yup.string().max(128),
                    email: event?.ticketing?.shippingDetails?.mandatory
                      ? Yup.string()
                          .max(128)
                          .email("Must be a valid email")
                          .required("Required")
                      : Yup.string().max(128).email("Must be a valid email"),
                    company: Yup.string().max(255),
                    deliveryNotes: Yup.string(),
                    address1: event?.ticketing?.shippingDetails?.mandatory
                      ? Yup.string().max(255).required("Required")
                      : Yup.string().max(255),
                    address2: Yup.string().max(255),
                    city: event?.ticketing?.shippingDetails?.mandatory
                      ? Yup.string().max(255).required("Required")
                      : Yup.string().max(255),
                    zip: Yup.string().max(128),
                    state: Yup.string().max(255),
                    country: event?.ticketing?.shippingDetails?.mandatory
                      ? Yup.string().max(255).required("Required")
                      : Yup.string().max(255)
                  })}
                  onSubmit={async (values) => {
                    if (event?.ticketing?.price > 0) {
                      setLoading(true);

                      setPlayerData({
                        ...playerData,
                        shippingDetails: values
                      });
                      setTicketModal(4);
                    } else {
                      await submitPlayer({
                        ...playerData,
                        shippingDetails: values
                      });
                    }
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                    setFieldValue
                  }) => (
                    <form onSubmit={handleSubmit}>
                      {transactionQueued?.queuedPosition === undefined &&
                        ticketModal === 3 && (
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography variant="h5" gutterBottom>
                                {t("shippingDetails")}
                              </Typography>
                              {Boolean(
                                event?.ticketing?.shippingDetails?.description
                              ) && (
                                <Typography variant="caption" gutterBottom>
                                  {
                                    event?.ticketing?.shippingDetails
                                      ?.description
                                  }
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="firstName"
                                label="First Name"
                                value={values.firstName}
                                variant="outlined"
                                fullWidth
                                required={
                                  event?.ticketing?.shippingDetails?.mandatory
                                    ? true
                                    : false
                                }
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.firstName && errors.firstName
                                )}
                                helperText={
                                  touched.firstName && errors.firstName
                                }
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="lastName"
                                label="Last Name"
                                value={values.lastName}
                                variant="outlined"
                                fullWidth
                                required={
                                  event?.ticketing?.shippingDetails?.mandatory
                                    ? true
                                    : false
                                }
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.lastName && errors.lastName
                                )}
                                helperText={touched.lastName && errors.lastName}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="email"
                                label="Email"
                                value={values.email}
                                variant="outlined"
                                fullWidth
                                required={
                                  event?.ticketing?.shippingDetails?.mandatory
                                    ? true
                                    : false
                                }
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  const trimmedString = e.target.value
                                    .trim()
                                    .toLowerCase();
                                  setFieldValue("email", trimmedString);
                                }}
                                error={Boolean(touched.email && errors.email)}
                                helperText={touched.email && errors.email}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="company"
                                label={t("company")}
                                value={values.company}
                                variant="outlined"
                                fullWidth
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.company && errors.company
                                )}
                                helperText={touched.company && errors.company}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                name="address1"
                                label={t("addressLine1")}
                                value={values.address1}
                                variant="outlined"
                                fullWidth
                                required={
                                  event?.ticketing?.shippingDetails?.mandatory
                                    ? true
                                    : false
                                }
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.address1 && errors.address1
                                )}
                                helperText={touched.address1 && errors.address1}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="address2"
                                label={t("addressLine2")}
                                value={values.address2}
                                variant="outlined"
                                fullWidth
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.address2 && errors.address2
                                )}
                                helperText={touched.address2 && errors.address2}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="city"
                                label={t("city")}
                                value={values.city}
                                variant="outlined"
                                fullWidth
                                required={
                                  event?.ticketing?.shippingDetails?.mandatory
                                    ? true
                                    : false
                                }
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(touched.city && errors.city)}
                                helperText={touched.city && errors.city}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="zip"
                                label={t("zipCode")}
                                value={values.zip}
                                variant="outlined"
                                fullWidth
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(touched.zip && errors.zip)}
                                helperText={touched.zip && errors.zip}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="state"
                                label={t("state")}
                                value={values.state}
                                variant="outlined"
                                fullWidth
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(touched.state && errors.state)}
                                helperText={touched.state && errors.state}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="country"
                                label={t("country")}
                                value={values.country}
                                variant="outlined"
                                fullWidth
                                required={
                                  event?.ticketing?.shippingDetails?.mandatory
                                    ? true
                                    : false
                                }
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.country && errors.country
                                )}
                                helperText={touched.country && errors.country}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="deliveryNotes"
                                label={t("deliveryNotes")}
                                value={values.deliveryNotes}
                                variant="outlined"
                                fullWidth
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.deliveryNotes && errors.deliveryNotes
                                )}
                                helperText={
                                  touched.deliveryNotes && errors.deliveryNotes
                                }
                                multiline
                                rows={3}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Button
                                color="error"
                                fullWidth
                                variant="outlined"
                                onClick={(e) => {
                                  setTicketModal(
                                    event?.ticketing?.price > 0 ? 2 : 1
                                  );
                                }}
                              >
                                {t("back")}
                              </Button>
                            </Grid>
                            <Grid item xs={6}>
                              <Button
                                color="success"
                                fullWidth
                                variant="contained"
                                type="submit"
                              >
                                {t("checkout")}
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                    </form>
                  )}
                </Formik>
                {transactionQueued?.queuedPosition === undefined &&
                  ticketModal === 4 && (
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        {event?.ticketing?.chargeType === "authorized" ? (
                          <AuthorizeHostedForm
                            event={event}
                            transactionQueued={transactionQueued}
                            playerData={playerData}
                            paymentData={paymentData}
                            loading={loading}
                            setLoading={setLoading}
                            setTicketModal={setTicketModal}
                          />
                        ) : event?.ticketing?.chargeType === "square" ? (
                          <SquareCheckout
                            event={event}
                            transactionQueued={transactionQueued}
                            playerData={playerData}
                            paymentData={paymentData}
                            loading={loading}
                            setLoading={setLoading}
                            setTicketModal={setTicketModal}
                            clientSecret={clientSecret}
                            setClientSecret={setClientSecret}
                            transactionId={transactionId}
                            setTransactionId={setTransactionId}
                            transactionOrderId={transactionOrderId}
                            setTransactionOrderId={setTransactionOrderId}
                          />
                        ) : (
                          <StripeCheckout
                            event={event}
                            transactionQueued={transactionQueued}
                            playerData={playerData}
                            paymentData={paymentData}
                            loading={loading}
                            setLoading={setLoading}
                            setTicketModal={setTicketModal}
                            cancelCheckout={cancelCheckout}
                            clientSecret={clientSecret}
                            setClientSecret={setClientSecret}
                            transactionId={transactionId}
                            setTransactionId={setTransactionId}
                            transactionOrderId={transactionOrderId}
                            setTransactionOrderId={setTransactionOrderId}
                          />
                        )}
                      </Grid>
                    </Grid>
                  )}
              </>
            )}
          </Box>
        </Box>
      </Modal>
      {(loading || preLoading) && <ModalLoadingFull />}
    </>
  );
}

export function ContactTOModal(props) {
  const { open, setOpen, event, authContext, subject, body, tGeneral } = props;
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [socials, setSocials] = useState(null);

  /*
  --- Steps ---
  0 - Loading
  1 - Socials Group
  2 - Email Form
  */

  function extractPath(input) {
    if (input.includes("/")) {
      return input.split("/").pop();
    }
    return input;
  }

  useEffect(() => {
    const go = async () => {
      try {
        let apiData = null;
        const tempSocials = {};

        if ((event?.gameStore?.id || event?.gameStoreId) && open === "store") {
          apiData = await getData(
            `/gamestores/${event?.gameStore?.id || event?.gameStoreId}`
          );
        } else {
          apiData = await getData(
            `/users/${event?.ownerId || event?.owner_Id || event?.owner?.id}`
          );
        }

        if (apiData?.socials) {
          if (apiData?.socials?.Discord)
            tempSocials.discord =
              "https://discord.com/users/" +
              extractPath(apiData?.socials?.Discord);

          if (apiData?.socials?.Twitter)
            tempSocials.twitter =
              "https://x.com/" + extractPath(apiData?.socials?.Twitter);

          if (apiData?.socials?.Facebook)
            tempSocials.facebook =
              "https://facebook.com/" + extractPath(apiData?.socials?.Facebook);

          if (apiData?.socials?.YouTube)
            tempSocials.youtube =
              "https://youtube.com/" + extractPath(apiData?.socials?.YouTube);

          if (apiData?.socials?.Instagram)
            tempSocials.instagram =
              "https://instagram.com/" +
              extractPath(apiData?.socials?.Instagram);

          if (apiData?.socials?.Twitch)
            tempSocials.twitch =
              "https://twitch.tv/" + extractPath(apiData?.socials?.Twitch);
        }

        setSocials(tempSocials);
      } catch (err) {
        postNotification("error", err);
      } finally {
        setStep(1);
        setLoading(false);
      }
    };

    go();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Modal
        disableAutoFocus
        open={Boolean(open)}
        onClose={() => {
          setOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "modal.background",
            color: "modal.text",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={1}
            sx={{
              mb: 1,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                Contact Organizer
              </Typography>
            </Grid>
          </Grid>

          {step === 0 || loading ? (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <LinearProgress />
              </Grid>
            </Grid>
          ) : step === 1 ? (
            <Grid container spacing={3} justifyContent="center">
              <Grid item>
                <Link
                  color="inherit"
                  underline="none"
                  aria-label="Company Support Email"
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    setStep(2);
                  }}
                >
                  <BCPEnvelope sx={{ fontSize: "24px", width: "24px" }} />
                </Link>
              </Grid>
              {socials?.facebook && (
                <Grid item>
                  <Link
                    color="inherit"
                    underline="none"
                    href={socials?.facebook}
                    target="_Blank"
                    aria-label="Company's Facebook account"
                  >
                    <BCPFacebook sx={{ fontSize: "24px", width: "24px" }} />
                  </Link>
                </Grid>
              )}
              {socials?.twitter && (
                <Grid item>
                  <Link
                    color="inherit"
                    underline="none"
                    href={socials?.twitter}
                    target="_Blank"
                    aria-label="Company's X/Twitter account"
                  >
                    <BCPTwitterX sx={{ fontSize: "24px", width: "24px" }} />
                  </Link>
                </Grid>
              )}
              {socials?.instagram && (
                <Grid item>
                  <Link
                    color="inherit"
                    underline="none"
                    href={socials?.instagram}
                    target="_Blank"
                    aria-label="Company's Instagram account"
                  >
                    <BCPInstagram sx={{ fontSize: "24px", width: "24px" }} />
                  </Link>
                </Grid>
              )}
              {socials?.discord && (
                <Grid item>
                  <Link
                    color="inherit"
                    underline="none"
                    href={socials?.discord}
                    target="_Blank"
                    aria-label="Company's Discord server"
                  >
                    <BCPDiscord sx={{ fontSize: "24px", width: "24px" }} />
                  </Link>
                </Grid>
              )}
              {socials?.twitch && (
                <Grid item>
                  <Link
                    color="inherit"
                    underline="none"
                    href={socials?.twitch}
                    target="_Blank"
                    aria-label="Company's Twitch account"
                  >
                    <BCPTwitch sx={{ fontSize: "24px", width: "24px" }} />
                  </Link>
                </Grid>
              )}
              {socials?.youtube && (
                <Grid item>
                  <Link
                    color="inherit"
                    underline="none"
                    href={socials?.youtube}
                    target="_Blank"
                    aria-label="Company's Youtube account"
                  >
                    <BCPYoutube sx={{ fontSize: "24px", width: "24px" }} />
                  </Link>
                </Grid>
              )}
              {(event?.gameStore?.id || event?.gameStoreId) &&
                open === "store" && (
                  <Grid item xs={12} sx={{ mt: -2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      href={`/organization/${
                        event?.gameStoreId || event?.gameStore?.id
                      }`}
                    >
                      View Game Store
                    </Button>
                  </Grid>
                )}
            </Grid>
          ) : (
            <Formik
              initialValues={{
                messageSubject: subject || event?.name || "",
                messageBody: body || "",
                fromName: `${
                  authContext?.authState?.identity?.firstName ||
                  authContext?.authState?.identity?.given_name ||
                  ""
                } ${
                  authContext?.authState?.identity?.lastName ||
                  authContext?.authState?.identity?.family_name ||
                  ""
                }`.trim(),
                fromEmail: authContext?.authState?.identity?.email || ""
              }}
              validationSchema={Yup.object().shape({
                fromName: Yup.string().max(255).required("Required"),
                fromEmail: Yup.string()
                  .max(128)
                  .email("Must be a valid email")
                  .required("Required"),
                messageSubject: Yup.string().max(70).required("Required"),
                messageBody: Yup.string().required("Required")
              })}
              onSubmit={async (values) => {
                setLoading(true);
                try {
                  await postData(`/events/${event?.id}/contactOrganizer`, {
                    body: values
                  });
                  setLoading(false);
                  postNotification(
                    "success",
                    tGeneral("notifications.success.emailSent")
                  );
                } catch (err) {
                  postNotification("error", err);
                }
                setLoading(false);

                return;
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                touched,
                values,
                setFieldValue
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        name="fromName"
                        label="Name"
                        value={values.fromName}
                        variant="outlined"
                        fullWidth
                        required
                        margin="dense"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(touched.fromName && errors.fromName)}
                        helperText={touched.fromName && errors.fromName}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="fromEmail"
                        label="Email"
                        value={values.fromEmail}
                        variant="outlined"
                        fullWidth
                        required
                        margin="dense"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          const trimmedString = e.target.value
                            .trim()
                            .toLowerCase();
                          setFieldValue("fromEmail", trimmedString);
                        }}
                        error={Boolean(touched.fromEmail && errors.fromEmail)}
                        helperText={touched.fromEmail && errors.fromEmail}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="messageSubject"
                        label="Subject"
                        value={values.messageSubject}
                        variant="outlined"
                        fullWidth
                        required
                        margin="dense"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched.messageSubject && errors.messageSubject
                        )}
                        helperText={
                          touched.messageSubject && errors.messageSubject
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="messageBody"
                        label="Body"
                        value={values.messageBody}
                        variant="outlined"
                        fullWidth
                        multiline
                        required
                        rows={5}
                        margin="dense"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched.messageBody && errors.messageBody
                        )}
                        helperText={touched.messageBody && errors.messageBody}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Button
                        color="error"
                        fullWidth
                        variant="outlined"
                        onClick={(e) => {
                          setOpen(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Grid>

                    <Grid item xs={6}>
                      <Button
                        color="success"
                        fullWidth
                        variant="contained"
                        type="submit"
                      >
                        Send
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          )}
        </Box>
      </Modal>
      {loading && <ModalLoadingFull />}
    </>
  );
}

function StripeCheckout(props) {
  const {
    event,
    transactionQueued,
    playerData,
    paymentData,
    setLoading,
    setTicketModal,
    clientSecret,
    setClientSecret,
    transactionId,
    setTransactionId,
    transactionOrderId,
    setTransactionOrderId
  } = props;
  const theme = useTheme();
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    const go = async () => {
      try {
        if (!transactionQueued?.id) {
          throw new Error("Invalid transaction info, please try again");
        }

        // Only runs initially once
        if (transactionQueued?.status === "Pending Checkout") {
          var stripeIntent = await postData(
            `/transactions/${transactionQueued.id}/paymentIntent`,
            {
              body: {
                [event?.format?.teamEvent || event?.format?.doublesEvent
                  ? "captainData"
                  : "playerData"]: playerData,
                paymentData: paymentData
              }
            }
          );

          setTransactionId(stripeIntent?.transactionId);
          setTransactionOrderId(stripeIntent?.transactionOrderId);
          setClientSecret(stripeIntent?.clientSecret);
        }
      } catch (err) {
        postNotification("error", err);
        setTicketModal(event?.ticketing?.shippingDetails?.requested ? 3 : 2);
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (clientSecret && !stripePromise && !stripeLoading) {
      setStripeLoading(true);
      loadStripeAsync()
        .then((stripe) => {
          setStripePromise(stripe);
          setStripeLoading(false);
        })
        .catch((error) => {
          console.error("Failed to load Stripe:", error);
          setStripeLoading(false);
        });
    }
  }, [clientSecret, stripePromise, stripeLoading]);

  const options = useMemo(() => {
    const appearance = {
      theme: theme?.palette?.mode === "dark" ? "night" : "stripe"
    };
    return {
      clientSecret,
      appearance
    };
  }, [clientSecret, theme?.palette?.mode]);

  return (
    <Box sx={{ width: "100%" }}>
      <Container
        sx={{
          pt: 2,
          pb: 2
        }}
        maxWidth="lg"
      >
        {clientSecret && stripePromise ? (
          <DynamicStripeElements options={options} stripe={stripePromise}>
            <DynamicCheckoutForm
              transactionQueued={transactionQueued}
              transactionId={transactionId}
              transactionOrderId={transactionOrderId}
              setTicketModal={setTicketModal}
              event={event}
            />
          </DynamicStripeElements>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100%",
              minWidth: "100%",
              minHeight: "250px"
            }}
          >
            {stripeLoading && <CircularProgress size={100} />}
          </Box>
        )}
      </Container>
    </Box>
  );
}

function DynamicStripeElements({ children, ...props }) {
  const [ElementsComponent, setElementsComponent] = useState(null);

  useEffect(() => {
    const loadElements = async () => {
      try {
        const { Elements } = await import("@stripe/react-stripe-js");
        setElementsComponent(() => Elements);
      } catch (error) {
        console.error("Failed to load Stripe Elements:", error);
      }
    };
    loadElements();
  }, []);

  if (!ElementsComponent) {
    return <CircularProgress />;
  }

  return React.createElement(ElementsComponent, props, children);
}

function ActualCheckoutForm({
  StripeComponents,
  transactionId,
  transactionOrderId,
  setTicketModal,
  event,
  isLoading,
  setIsLoading,
  message,
  setMessage
}) {
  const stripe = StripeComponents.useStripe();
  const elements = StripeComponents.useElements();

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe, setMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.protocol}//${window.location.host}/transaction/${transactionId}/receipt?orderId=${transactionOrderId}`
      }
    });

    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message);
    } else {
      setMessage("An unexpected error occured.");
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <ExpressCheckoutElement
            onConfirm={async (event) => {
              const { error: submitError } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                  return_url: `${window.location.protocol}//${window.location.host}/transaction/${transactionId}/receipt?orderId=${transactionOrderId}`
                }
              });

              if (submitError) {
                setMessage(submitError.message);
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sx={{ minHeight: "230px" }}>
          <StripeComponents.PaymentElement id="payment-element" />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Button
                disabled={isLoading}
                color="error"
                fullWidth
                variant="outlined"
                onClick={(e) => {
                  setTicketModal(
                    event?.ticketing?.shippingDetails?.requested ? 3 : 2
                  );
                }}
              >
                Back
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                disabled={isLoading || !stripe || !elements}
                fullWidth
                id="submit"
                variant="contained"
                color="success"
                type="submit"
              >
                Pay now
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isLoading && <ModalLoadingMessage open={isLoading} message={message} />}
    </form>
  );
}

function DynamicCheckoutForm(props) {
  const { transactionId, transactionOrderId, setTicketModal, event } = props;
  const [StripeComponents, setStripeComponents] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const loadStripeComponents = async () => {
      try {
        const stripeReact = await import("@stripe/react-stripe-js");
        setStripeComponents({
          useStripe: stripeReact.useStripe,
          useElements: stripeReact.useElements,
          PaymentElement: stripeReact.PaymentElement
        });
      } catch (error) {
        console.error("Failed to load Stripe components:", error);
      }
    };
    loadStripeComponents();
  }, []);

  if (!StripeComponents) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "230px"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ActualCheckoutForm
      StripeComponents={StripeComponents}
      transactionId={transactionId}
      transactionOrderId={transactionOrderId}
      setTicketModal={setTicketModal}
      event={event}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      message={message}
      setMessage={setMessage}
    />
  );
}
